import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import { FileText, Share2 } from "react-feather";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import html2pdf from "html2pdf.js";
import { API_BASE_URL } from "../../../../config";

import { cardSx, contentSx } from "./data/tokens";
import {
  copertureAssicurative,
  benchmarkSettore,
  dettagliAzienda,
  fattoriRischio,
} from "./data/mockData";
import { useAiAdvisorChat } from "./hooks/useAiAdvisorChat";
import {
  generateInsuranceAnalysis,
  isGeminiConfigured,
} from "../../../../services/geminiService";
import { setAiAnalysis } from "../../../../redux/slices/visureAiAnalysis";

import CopertureAssicurativeCard from "./components/CopertureAssicurative";
import RischioScoreCard from "./components/RischioScoreCard";
import BenchmarkCard from "./components/BenchmarkCard";
import FattoriRischioRadar from "./components/FattoriRischioRadar";
import AiAdvisorChat from "./components/AiAdvisorChat";
import DettagliAziendaCard from "./components/DettagliAziendaCard";
import ShareDialog from "./components/ShareDialog";
import QuestionnaireDialog from "./components/QuestionnaireDialog";

export default function Generale({ report, analitics }) {
  const theme = useTheme();
  const { idUID } = useParams();
  const dispatch = useDispatch();

  // Controlla se analitics contiene dati validi
  const hasValidAnalytics =
    analitics &&
    Object.keys(analitics).length > 0 &&
    analitics.copertureAssicurative &&
    Array.isArray(analitics.copertureAssicurative) &&
    analitics.copertureAssicurative.length > 0;

  // Recupera l'analisi dal Redux store
  const aiAnalysis = useSelector(
    (state) => state.visureAiAnalysis.analyses[idUID],
  );

  // Debug log
  console.log("Analitics ricevute:", analitics);
  console.log("hasValidAnalytics:", hasValidAnalytics);
  console.log("aiAnalysis esistente:", aiAnalysis);

  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [dettagliAziendaTab, setDettagliAziendaTab] = useState(0);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [preventivoOpen, setPreventivoOpen] = useState(false);
  const [preventivoCopertura, setPreventivoCopertura] = useState(null);

  // AI Analysis state
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(
    !aiAnalysis && !hasValidAnalytics,
  );
  const [analysisError, setAnalysisError] = useState(null);

  const shareLink = `${window.location.origin}/pages/visuredetails/${idUID}`;

  const chat = useAiAdvisorChat(report, aiAnalysis);
  // Load AI analysis on mount (solo se non è già in Redux e non è in analitics)
  useEffect(() => {
    // Se abbiamo analitics validi dall'API, usali e salvali nel Redux
    if (hasValidAnalytics && !aiAnalysis) {
      console.log("Using analitics from API:", analitics);
      dispatch(setAiAnalysis({ idUID, analysis: analitics }));
      setIsLoadingAnalysis(false);
      return;
    }

    // Se abbiamo già l'analisi nel Redux, non ricarichiamo
    if (aiAnalysis) {
      setIsLoadingAnalysis(false);
      return;
    }

    const loadAnalysis = async () => {
      if (!isGeminiConfigured()) {
        setAnalysisError("Gemini AI non configurato. Usa i dati mock.");
        setIsLoadingAnalysis(false);
        return;
      }

      try {
        setIsLoadingAnalysis(true);
        setAnalysisError(null);
        const analysis = await generateInsuranceAnalysis(report);
        console.log("AI Analysis generated:", analysis);

        // Salva l'analisi nel database via API
        const token = sessionStorage.getItem("token");
        try {
          await fetch(`${API_BASE_URL}/api/visure`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: "Bearer " + token,
            },
            body: JSON.stringify({
              idUid: idUID,
              dataAnalitics: analysis,
            }),
          });
        } catch (saveError) {
          console.error("Error saving analysis to database:", saveError);
          // Non blocchiamo il flusso se il salvataggio fallisce
        }

        // Salva l'analisi nel Redux store
        dispatch(setAiAnalysis({ idUID, analysis }));
        setIsLoadingAnalysis(false);
      } catch (error) {
        console.error("Error loading AI analysis:", error);
        setAnalysisError(error.message);
        setIsLoadingAnalysis(false);
      }
    };

    loadAnalysis();
  }, [report, idUID, aiAnalysis, hasValidAnalytics, analitics, dispatch]);

  // Extract scoreRisk from report (the same value shown in the main header)
  const scoreRiskFromReport =
    report?.creditScore?.currentCreditRating?.providerValue?.value || null;

  // Use AI data if available, otherwise fallback to mock data
  const copertureData =
    aiAnalysis?.copertureAssicurative || copertureAssicurative;
  const fattoriRischioData = aiAnalysis?.fattoriRischio || fattoriRischio;
  const benchmarkData = aiAnalysis?.benchmarkSettore || benchmarkSettore;
  const riskScoreData = aiAnalysis
    ? {
        score: aiAnalysis.riskScore, // Keep AI calculated risk score for insurance factor
        color: aiAnalysis.riskColor,
        description: aiAnalysis.riskDescription,
        text: aiAnalysis.riskText,
      }
    : null;

  const radarData = useMemo(
    () => [
      {
        subject: "Finanziario",
        value: fattoriRischioData.finanziario,
        fullMark: 10,
      },
      {
        subject: "Settoriale",
        value: fattoriRischioData.settoriale,
        fullMark: 10,
      },
      {
        subject: "Geografico",
        value: fattoriRischioData.geografico,
        fullMark: 10,
      },
      {
        subject: "Dimensionale",
        value: fattoriRischioData.dimensionale,
        fullMark: 10,
      },
      {
        subject: "Operativo",
        value: fattoriRischioData.operativo,
        fullMark: 10,
      },
    ],
    [fattoriRischioData],
  );

  const knownPreventivoData = useMemo(
    () => ({
      ragioneSociale: report?.alternateSummary?.businessName || "",
      partitaIVA:
        report?.alternateSummary?.vatRegistrationNumber ||
        report?.alternateSummary?.taxCode ||
        "",
      settore:
        report?.alternateSummary?.mainActivity?.description ||
        report?.alternateSummary?.activityClassifications?.description ||
        "",
      fatturato: report?.companySummary?.latestTurnoverFigure?.value || "",
    }),
    [report],
  );

  const handleCreaPreventivo = (copertura) => {
    setPreventivoCopertura(copertura);
    setPreventivoOpen(true);
  };

  const handleSubmitPreventivo = async (data) => {
    // TODO: collegare invio al backend; per ora log
    console.log("Questionario preventivo inviato", data);
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSendEmail = async (email) => {
    // TODO: Implementare chiamata API per inviare email
    // Per ora simuliamo con una promise
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        console.log("Email inviata a:", email);
        console.log("Report:", report?.alternateSummary?.businessName);
        console.log("Link:", shareLink);
        resolve();
      }, 1000);
    });
  };

  const handleGeneratePDF = () => {
    // Crea HTML con tutto il contenuto del report
    const html = `
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              color: #333;
              line-height: 1.6;
              padding: 40px;
            }
            .page-break {
              page-break-after: always;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 3px solid #1976d2;
              padding-bottom: 20px;
            }
            .header h1 {
              font-size: 28px;
              margin-bottom: 10px;
              color: #1976d2;
            }
            .header p {
              font-size: 14px;
              color: #666;
            }
            .section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 15px;
              border-left: 4px solid #1976d2;
              padding-left: 10px;
            }
            .content-box {
              background-color: #f5f5f5;
              padding: 15px;
              border-radius: 5px;
              margin-bottom: 15px;
              page-break-inside: avoid;
            }
            .row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
            }
            .row-label {
              font-weight: bold;
              color: #555;
            }
            .row-value {
              color: #333;
            }
            .risk-score-box {
              text-align: center;
              padding: 20px;
              background-color: #f0f0f0;
              border-radius: 10px;
              margin: 20px 0;
              page-break-inside: avoid;
            }
            .risk-score-number {
              font-size: 48px;
              font-weight: bold;
              color: ${riskScoreData?.color || "#6dc797"};
              margin-bottom: 10px;
            }
            .risk-score-text {
              font-size: 14px;
              color: #666;
            }
            .coperture-list {
              list-style: none;
            }
            .copertura-item {
              background-color: #fff;
              border: 1px solid #ddd;
              padding: 12px;
              margin-bottom: 10px;
              border-radius: 5px;
              page-break-inside: avoid;
            }
            .copertura-tipo {
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 5px;
            }
            .copertura-details {
              font-size: 13px;
              color: #666;
            }
            .benchmark-table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: avoid;
            }
            .benchmark-table tr {
              page-break-inside: avoid;
            }
            .benchmark-table th {
              background-color: #1976d2;
              color: white;
              padding: 10px;
              text-align: left;
              border: 1px solid #ddd;
            }
            .benchmark-table td {
              padding: 10px;
              border: 1px solid #ddd;
            }
            .benchmark-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 12px;
              color: #999;
              border-top: 1px solid #ddd;
              padding-top: 20px;
            }
          </style>
        </head>
        <body>
          <!-- HEADER -->
          <div class="header">
            <h1>Report Analisi Assicurativa</h1>
            <p>Azienda: <strong>${
              report?.alternateSummary?.businessName || "N/A"
            }</strong></p>
            <p>Data Report: ${new Date().toLocaleDateString("it-IT")}</p>
            <p>P.IVA: ${
              report?.alternateSummary?.vatRegistrationNumber || "N/A"
            }</p>
          </div>

          <!-- SEZIONE 1: DATI AZIENDALI -->
          <div class="section">
            <div class="section-title">1. Informazioni Aziendali</div>
            <div class="content-box">
              <div class="row">
                <span class="row-label">Ragione Sociale:</span>
                <span class="row-value">${
                  report?.alternateSummary?.businessName || "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Partita IVA:</span>
                <span class="row-value">${
                  report?.alternateSummary?.vatRegistrationNumber || "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Codice Fiscale:</span>
                <span class="row-value">${
                  report?.alternateSummary?.taxCode || "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Forma Giuridica:</span>
                <span class="row-value">${
                  report?.alternateSummary?.legalForm || "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Data Costituzione:</span>
                <span class="row-value">${
                  report?.alternateSummary?.incorporationDate
                    ? new Date(
                        report.alternateSummary.incorporationDate,
                      ).toLocaleDateString("it-IT")
                    : "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Indirizzo:</span>
                <span class="row-value">${
                  report?.alternateSummary?.contactAddress?.street || ""
                } ${
      report?.alternateSummary?.contactAddress?.houseNumber || ""
    }, ${report?.alternateSummary?.contactAddress?.postalCode || ""} ${
      report?.alternateSummary?.contactAddress?.city || ""
    }</span>
              </div>
              <div class="row">
                <span class="row-label">Stato Azienda:</span>
                <span class="row-value">${
                  report?.alternateSummary?.companyStatus?.description || "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Numero Dipendenti:</span>
                <span class="row-value">${
                  report?.alternateSummary?.numberOfEmployees || "N/A"
                }</span>
              </div>
              <div class="row">
                <span class="row-label">Ultimo Fatturato:</span>
                <span class="row-value">€${
                  report?.companySummary?.latestTurnoverFigure?.value?.toLocaleString(
                    "it-IT",
                  ) || "N/A"
                }</span>
              </div>
            </div>
          </div>

          <!-- SEZIONE 2: INDICE DI RISCHIO -->
          <div class="section">
            <div class="section-title">2. Indice di Rischio Complessivo</div>
            <div class="risk-score-box">
              <div class="risk-score-number">${
                riskScoreData?.score || "N/A"
              }</div>
              <div style="font-weight: bold; font-size: 16px; color: ${
                riskScoreData?.color || "#6dc797"
              }; margin-bottom: 10px;">
                ${riskScoreData?.description || "N/A"}
              </div>
              <div class="risk-score-text">
                ${riskScoreData?.text || "N/A"}
              </div>
            </div>
          </div>

          <!-- SEZIONE 3: FATTORI DI RISCHIO -->
          <div class="section">
            <div class="section-title">3. Fattori di Rischio</div>
            <div class="content-box">
              <div class="row">
                <span class="row-label">Finanziario:</span>
                <span class="row-value">${
                  fattoriRischioData?.finanziario || "N/A"
                }/10</span>
              </div>
              <div class="row">
                <span class="row-label">Settoriale:</span>
                <span class="row-value">${
                  fattoriRischioData?.settoriale || "N/A"
                }/10</span>
              </div>
              <div class="row">
                <span class="row-label">Geografico:</span>
                <span class="row-value">${
                  fattoriRischioData?.geografico || "N/A"
                }/10</span>
              </div>
              <div class="row">
                <span class="row-label">Dimensionale:</span>
                <span class="row-value">${
                  fattoriRischioData?.dimensionale || "N/A"
                }/10</span>
              </div>
              <div class="row">
                <span class="row-label">Operativo:</span>
                <span class="row-value">${
                  fattoriRischioData?.operativo || "N/A"
                }/10</span>
              </div>
            </div>
          </div>

          <!-- SEZIONE 4: COPERTURE ASSICURATIVE CONSIGLIATE -->
          <div class="section">
            <div class="section-title">4. Coperture Assicurative Consigliate</div>
            <ul class="coperture-list">
              ${
                copertureData
                  ?.map(
                    (c) => `
                <li class="copertura-item">
                  <div class="copertura-tipo">${c.tipo || "N/A"}</div>
                  <div class="copertura-details">
                    <strong>Priorità:</strong> ${c.priorita || "N/A"} |
                    <strong>Premio stimato:</strong> €${
                      c.importo?.toLocaleString("it-IT") || "N/A"
                    }/anno |
                    <strong>Massimale consigliato:</strong> ${
                      c.massimoStimato || "N/A"
                    }
                  </div>
                  <div class="copertura-details" style="margin-top: 8px;">
                    ${c.descrizione || "N/A"}
                  </div>
                </li>
              `,
                  )
                  .join("") || "<p>Nessuna copertura disponibile</p>"
              }
            </ul>
              <div class="content-box" style="text-align: center; margin-top: 20px;">
                <p style="font-size: 16px; font-weight: 700; margin-bottom: 8px;">Prenota la tua Validazione</p>
                <p style="font-size: 14px; color: #555; margin-bottom: 16px;">
                  Questo report è un punto di partenza. Trasforma questi numeri in un piano d'azione reale con una breve call di 15 minuti.
                </p>
                <a href="mailto:?subject=Richiesta%20consulenza%20assicurativa&body=Vorrei%20prenotare%20una%20call%20di%2015%20minuti%20per%20validare%20il%20report." 
                   style="display: inline-block; background-color: #1976d2; color: #fff; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: 700; letter-spacing: 0.2px;">
                  PRENOTA CONSULENZA GRATUITA
                </a>
              </div>

              <p style="font-size: 12px; color: #777; margin-top: 16px; text-align: center;">
                Disclaimer: Questo report è un'analisi teorica basata sui dati forniti. Non costituisce sollecitazione all'investimento o proposta contrattuale. Consultare un intermediario abilitato per dettagli.
              </p>
          </div>

          <!-- SEZIONE 5: BENCHMARK DI SETTORE -->
          <div class="section">
            <div class="section-title">5. Benchmark di Settore</div>
            <table class="benchmark-table">
              <thead>
                <tr>
                  <th>Metrica</th>
                  <th>Azienda</th>
                  <th>Media Settore</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Risk Score</td>
                  <td><strong>${scoreRiskFromReport || "N/A"}</strong></td>
                  <td>${benchmarkData?.mediaSector || "N/A"}</td>
                </tr>
                <tr>
                  <td>Fatturato</td>
                  <td><strong>€${
                    benchmarkData?.fatturato?.toLocaleString("it-IT") || "N/A"
                  }</strong></td>
                  <td>€${
                    benchmarkData?.mediaFatturato?.toLocaleString("it-IT") ||
                    "N/A"
                  }</td>
                </tr>
                <tr>
                  <td>Margine Utile Netto</td>
                  <td><strong>${
                    benchmarkData?.margineUtile || "N/A"
                  }</strong></td>
                  <td>${benchmarkData?.mediaUtile || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- FOOTER -->
          <div class="footer">
            <p>Questo report è stato generato automaticamente il ${new Date().toLocaleDateString(
              "it-IT",
            )} alle ${new Date().toLocaleTimeString("it-IT")}</p>
            <p>Per informazioni dettagliate, si consiglia di consultare un esperto assicurativo.</p>
          </div>
        </body>
      </html>
    `;

    // Opzioni per html2pdf
    const options = {
      margin: 10,
      filename: `Report_Assicurativo_${
        report?.alternateSummary?.businessName?.replace(/\s+/g, "_") ||
        "Azienda"
      }_${new Date().toLocaleDateString("it-IT").replace(/\//g, "-")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
    };

    // Genera il PDF
    html2pdf().set(options).from(html).save();
  };

  if (isLoadingAnalysis) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={60} />
          <Alert severity="info">
            Generazione analisi assicurativa con AI in corso...
          </Alert>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {analysisError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {analysisError} - Visualizzazione dati di esempio.
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3.5 }}
        justifyContent="flex-end"
      >
        <Button
          variant="outlined"
          startIcon={<FileText size={18} />}
          onClick={handleGeneratePDF}
        >
          Genera Report PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<Share2 size={18} />}
          onClick={() => setShareDialogOpen(true)}
        >
          Condividi Analisi
        </Button>
      </Stack>

      <Grid container spacing={{ xs: 2.5, md: 3.5 }}>
        <Grid item xs={12} lg={8}>
          <CopertureAssicurativeCard
            theme={theme}
            cardSx={cardSx}
            contentSx={contentSx}
            items={copertureData}
            expanded={expandedAccordion}
            onExpandedChange={setExpandedAccordion}
            onCreaPreventivo={handleCreaPreventivo}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <RischioScoreCard
            theme={theme}
            cardSx={cardSx}
            contentSx={contentSx}
            riskData={riskScoreData}
          />
        </Grid>

        <Grid item xs={12} lg={8}>
          <BenchmarkCard
            theme={theme}
            cardSx={cardSx}
            contentSx={contentSx}
            data={benchmarkData}
            riskScore={scoreRiskFromReport}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <FattoriRischioRadar
            theme={theme}
            cardSx={cardSx}
            contentSx={contentSx}
            radarData={radarData}
          />
        </Grid>

        <Grid item xs={12} lg={8}>
          <AiAdvisorChat
            theme={theme}
            cardSx={cardSx}
            contentSx={contentSx}
            {...chat}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <DettagliAziendaCard
            theme={theme}
            cardSx={cardSx}
            contentSx={contentSx}
            tab={dettagliAziendaTab}
            onTabChange={setDettagliAziendaTab}
            data={dettagliAzienda}
          />
        </Grid>
      </Grid>

      <ShareDialog
        theme={theme}
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        link={shareLink}
        linkCopied={linkCopied}
        onCopyLink={handleCopyLink}
        onSendEmail={handleSendEmail}
      />

      <QuestionnaireDialog
        open={preventivoOpen}
        onClose={() => setPreventivoOpen(false)}
        onSubmit={handleSubmitPreventivo}
        copertura={preventivoCopertura}
        knownData={knownPreventivoData}
      />
    </Box>
  );
}
